<template>
  <div class="gd-root">
    <div class="gd-topbar">
      <div class="gd-info">
        <span class="gd-title">{{ $t('tool.GeometryDraw.title') }}</span>
        <span class="gd-hint">点击按钮添加图形 · 拖拽节点调整 · 双击编辑标签</span>
      </div>
      <div class="gd-actions">
        <el-button size="small" plain icon="el-icon-picture-outline" @click="exportPng">导出 PNG</el-button>
        <el-button size="small" plain icon="el-icon-document" @click="exportSvg">导出 SVG</el-button>
        <el-button size="small" plain icon="el-icon-refresh" @click="clearAll">{{ $t("common.action.clear") }}</el-button>
      </div>
    </div>

    <!-- 工具栏 -->
    <div class="gd-toolbar">
      <span class="gd-tb-label">绘制：</span>
      <button v-for="t in tools" :key="t.key" :class="{ on: tool === t.key }" @click="setTool(t.key)" :title="t.title">{{ t.label }}</button>
      <span class="gd-tb-sep">|</span>
      <button @click="undo" title="撤销">↩</button>
      <span class="gd-tb-sep">|</span>
      <label class="gd-tb-check"><input type="checkbox" v-model="showGrid" @change="draw" /> 网格</label>
      <label class="gd-tb-check"><input type="checkbox" v-model="showAxes" @change="draw" /> 坐标轴</label>
      <span class="gd-tb-hint">{{ toolHint }}</span>
    </div>

    <!-- 快捷模板 -->
    <div class="gd-templates">
      <span class="gd-tpl-label">模板：</span>
      <el-button size="mini" @click="loadTemplate('rightTri')">直角△</el-button>
      <el-button size="mini" @click="loadTemplate('isosceles')">等腰△</el-button>
      <el-button size="mini" @click="loadTemplate('equilateral')">等边△</el-button>
      <el-button size="mini" @click="loadTemplate('square')">正方形</el-button>
      <el-button size="mini" @click="loadTemplate('circle')">圆</el-button>
    </div>

    <!-- 函数表达式 -->
    <div class="gd-fn-row">
      <span class="gd-fn-label">y =</span>
      <el-input v-model="fnInput" size="small" placeholder="如: sin(x), x^2, 2*x+1" @keyup.enter="addFunction" style="flex:1;max-width:300px" />
      <el-button size="small" type="primary" @click="addFunction">添加函数</el-button>
      <span class="gd-fn-presets">
        <span v-for="ex in fnPresets" :key="ex" class="gd-fn-chip" @click="fnInput=ex;addFunction()">{{ ex }}</span>
      </span>
    </div>

    <!-- 画布 -->
    <div class="gd-canvas-wrap"
      @mousedown="onMouseDown" @mousemove="onMouseMove" @mouseup="onMouseUp"
      @dblclick="onDblClick" @wheel.prevent="onWheel"
    >
      <canvas ref="c" :width="W" :height="H" />
      <div class="gd-cursor" v-if="cursor">{{ cursor }}</div>
    </div>

    <!-- 函数列表 -->
    <div class="gd-fn-list" v-if="functions.length">
      <div v-for="(fn,i) in functions" :key="fn.id" class="gd-fn-item">
        <span class="gd-fn-dot" :style="{background:fn.color}"></span>
        <span class="gd-fn-expr">y = {{ fn.expr }}</span>
        <el-button size="mini" type="text" class="del" @click="functions.splice(i,1);draw()">{{ $t("common.action.delete") }}</el-button>
      </div>
    </div>

    <!-- 选中元素的属性 -->
    <div class="gd-props" v-if="selected">
      <span class="gd-prop-label">选中：{{ selected.label || selected.type }}</span>
      <el-input v-if="selected.type==='label'" v-model="selected.text" size="mini" style="width:100px" @change="draw" />
      <el-button size="mini" type="danger" plain @click="deleteSelected">{{ $t("common.action.delete") }}</el-button>
      <el-button size="mini" plain @click="selected=null;draw()">取消选中</el-button>
    </div>
  </div>
</template>

<script>
var uid = 0
var COLORS = ['#e74c3c','#4e7cff','#67c23a','#e6a23c','#9b59b6','#1abc9c']
var LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

function dist(x1,y1,x2,y2) { return Math.sqrt((x1-x2)*(x1-x2)+(y1-y2)*(y1-y2)) }

export default {
  name: 'GeometryDrawTool',
  props: { config: { type: Object, default: function() { return {} } } },
  data: function() {
    return {
      W: 780, H: 500,
      tool: '',          // point|line|circle|label|move
      elements: [],      // { id, type, x, y, label, x1,y1,x2,y2, cx,cy,r, text, color }
      functions: [],     // { id, expr, fn, color }
      fnInput: '',
      fnPresets: ['x^2','sin(x)','cos(x)','exp(x)','1/x','sqrt(x)','x^3-3*x'],
      cursor: null,
      selected: null,
      dragging: null,     // 拖拽中的元素
      dragOffset: null,
      _preview: null,     // 预览状态 { type:'line'|'circle', x1, y1 } 或 { type, cx, cy }
      _angleLines: [],    // 角度标注：点击两条线
      showGrid: true,
      showAxes: true,
      labelIdx: 0,
      fnColorIdx: 0,
      undoStack: [],
      tools: [
        { key: 'point', label: '·点', title: '点击画布添加点' },
        { key: 'line', label: '╱线', title: '点击起点 → 移动预览 → 点击终点' },
        { key: 'circle', label: '⊙圆', title: '点击圆心 → 移动预览 → 点击定半径' },
        { key: 'label', label: 'A标签', title: '点击画布添加文字标签' },
        { key: 'tangent', label: '⌒切线', title: '点击函数曲线或圆上的点画切线' },
        { key: 'angle', label: '∠角度', title: '依次点击顶点→边1→边2标注角度' },
        { key: 'rightAngle', label: '∟直角', title: '点击直角顶点→两边标注直角符号' },
        { key: 'move', label: '✋移动', title: '拖拽移动元素' }
      ]
    }
  },
  computed: {
    toolHint: function() {
      var m = { point:'点击画布添加点', line:'按住拖拽画线段', circle:'按住拖拽画圆', label:'点击放置标签', tangent:'点击曲线或圆上的点画切线', angle:'依次点击两条线 → 输入角度', rightAngle:'依次点击两条直角边 → 自动标直角', move:'拖拽移动元素' }
      return this.tool ? m[this.tool] || '' : '选择上方工具开始绘制'
    }
  },
  mounted: function() {
    var self = this
    this.$nextTick(function() { self.draw() })
    document.addEventListener('keydown', function(e) { if (e.key === 'Escape') { self._preview = null; self.draw() } })
    document.addEventListener('mouseup', function(e) { self.dragging = null })
  },

  methods: {
    // ── 坐标转换 ──────────────────────────
    toWorld: function(mx, my) {
      var pad = 40, w = this.W - pad * 2, h = this.H - pad * 2
      return { x: (mx - pad) / w * 12 - 6, y: 6 - (my - pad) / h * 8 }
    },
    toScreen: function(x, y) {
      var pad = 40, w = this.W - pad * 2, h = this.H - pad * 2
      return { x: pad + (x + 6) / 12 * w, y: pad + (6 - y) / 8 * h }
    },

    // ── 绘图 ──────────────────────────────
    draw: function() {
      var c = this.$refs.c, ctx = c.getContext('2d')
      var W = this.W, H = this.H, pad = 40, self = this
      ctx.clearRect(0, 0, W, H)
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H)

      // 网格
      if (this.showGrid) {
        ctx.strokeStyle = '#f0f2f5'; ctx.lineWidth = 0.5
        for (var i = -5; i <= 5; i++) {
          var sx = this.toScreen(i, 0).x, sy = this.toScreen(0, i).y
          ctx.beginPath(); ctx.moveTo(sx, pad); ctx.lineTo(sx, H - pad); ctx.stroke()
          ctx.beginPath(); ctx.moveTo(pad, sy); ctx.lineTo(W - pad, sy); ctx.stroke()
          ctx.fillStyle = '#bbb'; ctx.font = '10px sans-serif'; ctx.textAlign = 'center'
          if (i !== 0) ctx.fillText(i, sx, this.toScreen(0, -0.1).y + 14)
          ctx.textAlign = 'right'
          if (i !== 0) ctx.fillText(i, this.toScreen(-0.15, 0).x - 8, sy + 4)
        }
      }
      // 坐标轴
      if (this.showAxes) {
        var zx = this.toScreen(0, 0).x, zy = this.toScreen(0, 0).y
        ctx.strokeStyle = '#999'; ctx.lineWidth = 1.2
        ctx.beginPath(); ctx.moveTo(pad, zy); ctx.lineTo(W - pad, zy); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(zx, pad); ctx.lineTo(zx, H - pad); ctx.stroke()
        ctx.fillStyle = '#888'; ctx.textAlign = 'left'
        ctx.fillText('x', W - pad + 2, zy + 4)
        ctx.fillText('y', zx + 4, pad - 4)
        ctx.fillText('O', zx - 14, zy + 14)
      }

      // 绘制所有元素
      var self = this
      this.elements.forEach(function(el) {
        ctx.strokeStyle = el.color || '#333'; ctx.fillStyle = el.color || '#333'; ctx.lineWidth = el.lineWidth || 2
        var s, s2
        switch (el.type) {
          case 'point':
            s = self.toScreen(el.x, el.y)
            ctx.fillStyle = el.color || '#409EFF'
            ctx.beginPath(); ctx.arc(s.x, s.y, 5, 0, Math.PI * 2); ctx.fill()
            ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke()
            if (el.label) { ctx.fillStyle = '#333'; ctx.font = 'bold 13px sans-serif'; ctx.fillText(el.label, s.x + 8, s.y - 6) }
            break
          case 'line':
            s = self.toScreen(el.x1, el.y1); s2 = self.toScreen(el.x2, el.y2)
            ctx.strokeStyle = el.color || '#333'; ctx.lineWidth = el.lineWidth || 2
            if (el.dashed) ctx.setLineDash([6, 4])
            ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(s2.x, s2.y); ctx.stroke()
            ctx.setLineDash([])
            if (el._tangent) {
              var mx2 = (s.x + s2.x) / 2, my2 = (s.y + s2.y) / 2
              var slope = (el.y2 - el.y1) / (el.x2 - el.x1)
              ctx.fillStyle = '#e74c3c'; ctx.font = '10px sans-serif'
              ctx.fillText('k=' + (isFinite(slope) ? slope.toFixed(3) : '∞'), mx2 + 8, my2 - 6)
            }
            break
          case 'circle':
            s = self.toScreen(el.cx, el.cy)
            var rx = Math.abs(self.toScreen(el.cx + el.r, el.cy).x - s.x)
            ctx.strokeStyle = el.color || '#333'; ctx.lineWidth = el.lineWidth || 2
            ctx.beginPath(); ctx.arc(s.x, s.y, rx, 0, Math.PI * 2); ctx.stroke()
            break
          case 'label':
            s = self.toScreen(el.x, el.y)
            ctx.fillStyle = el.color || '#333'; ctx.font = '13px sans-serif'
            ctx.fillText(el.text || '', s.x, s.y)
            break
          case 'polygon':
            ctx.strokeStyle = el.color || '#333'; ctx.fillStyle = (el.fill || 'transparent'); ctx.lineWidth = el.lineWidth || 2
            ctx.beginPath()
            var pts = el.points || []
            for (var pi = 0; pi < pts.length; pi++) { var pp = self.toScreen(pts[pi][0], pts[pi][1]); if (pi===0) ctx.moveTo(pp.x, pp.y); else ctx.lineTo(pp.x, pp.y) }
            ctx.closePath(); if (el.fill && el.fill !== 'transparent') ctx.fill(); ctx.stroke()
            break
          case 'angleArc':
            var sv = self.toScreen(el.vx, el.vy)
            var sa = self.toScreen(el.ax, el.ay)
            var sb = self.toScreen(el.bx, el.by)
            var a1 = Math.atan2(sa.y - sv.y, sa.x - sv.x)
            var a2 = Math.atan2(sb.y - sv.y, sb.x - sv.x)
            var r = 30
            ctx.strokeStyle = el.color || '#333'; ctx.lineWidth = 1.5
            // 选择较小内角（≤180°）画弧，标注在弧中点上方10px
            var sweepCw = (a1 - a2 + 2 * Math.PI) % (2 * Math.PI)
            var ccw, midA
            if (sweepCw <= Math.PI) {
              ccw = true; midA = a1 + sweepCw / 2
            } else {
              ccw = false; midA = a1 - (2 * Math.PI - sweepCw) / 2
            }
            ctx.beginPath(); ctx.arc(sv.x, sv.y, r, a1, a2, ccw)
            ctx.stroke()
            var lx = sv.x + (r + 10) * Math.cos(midA)
            var ly = sv.y + (r + 10) * Math.sin(midA)
            ctx.fillStyle = '#333'; ctx.font = 'bold 12px sans-serif'; ctx.textAlign = 'center'
            ctx.fillText(el.deg + '°', lx, ly)
            break
          case 'rightAngle':
            var rv = self.toScreen(el.vx, el.vy)
            var ra = self.toScreen(el.ax, el.ay)
            var rb = self.toScreen(el.bx, el.by)
            var ra1 = Math.atan2(ra.y - rv.y, ra.x - rv.x)
            var ra2 = Math.atan2(rb.y - rv.y, rb.x - rv.x)
            var rs = 14
            var rx1 = rv.x + rs * Math.cos(ra1), ry1 = rv.y + rs * Math.sin(ra1)
            var rx2 = rv.x + rs * Math.cos(ra2), ry2 = rv.y + rs * Math.sin(ra2)
            var rx3 = rx1 + rs * Math.cos(ra2), ry3 = ry1 + rs * Math.sin(ra2)
            ctx.strokeStyle = el.color || '#333'; ctx.lineWidth = 1.5
            ctx.beginPath(); ctx.moveTo(rx1, ry1); ctx.lineTo(rx3, ry3); ctx.lineTo(rx2, ry2); ctx.stroke()
            break
        }
      })

      // 函数曲线
      var pad = 40, self2 = this
      this.functions.forEach(function(fn) {
        if (!fn.fn) return
        ctx.strokeStyle = fn.color; ctx.lineWidth = 2; ctx.lineJoin = 'round'
        ctx.beginPath()
        var started = false, step = 12 / 800
        for (var x = -6; x <= 6; x += step) {
          var y = fn.fn(x)
          if (isNaN(y) || !isFinite(y) || Math.abs(y) > 1e6) { started = false; continue }
          var sy = self2.toScreen(x, y).y
          if (sy < pad || sy > self2.H - pad) { started = false; continue }
          var sx = self2.toScreen(x, y).x
          if (!started) { ctx.moveTo(sx, sy); started = true }
          else ctx.lineTo(sx, sy)
        }
        ctx.stroke()
      })

      // 画线/画圆预览
      if (this._preview) {
        ctx.strokeStyle = '#4e7cff'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4])
        if (this._preview.type === 'line') {
          var ps = this.toScreen(this._preview.x1, this._preview.y1)
          var pe = this.toScreen(this._preview.x2, this._preview.y2)
          ctx.beginPath(); ctx.moveTo(ps.x, ps.y); ctx.lineTo(pe.x, pe.y); ctx.stroke()
        } else if (this._preview.type === 'circle') {
          var pc = this.toScreen(this._preview.cx, this._preview.cy)
          var pr = Math.abs(this.toScreen(this._preview.cx + this._preview.r, this._preview.cy).x - pc.x)
          ctx.beginPath(); ctx.arc(pc.x, pc.y, pr, 0, Math.PI * 2); ctx.stroke()
          var prp = this.toScreen(this._preview.cx + this._preview.r, this._preview.cy)
          ctx.beginPath(); ctx.moveTo(pc.x, pc.y); ctx.lineTo(prp.x, prp.y); ctx.stroke()
        }
        ctx.setLineDash([])
      }

      // 角度工具选中线高亮
      if (this._angleLines.length === 1) {
        var hl = this._angleLines[0], hls = this.toScreen(hl.x1, hl.y1), hle = this.toScreen(hl.x2, hl.y2)
        ctx.strokeStyle = '#e6a23c'; ctx.lineWidth = 3; ctx.setLineDash([])
        ctx.beginPath(); ctx.moveTo(hls.x, hls.y); ctx.lineTo(hle.x, hle.y); ctx.stroke()
      }

      // 选中高亮
      if (this.selected) {
        var sel = this.selected; ctx.strokeStyle = '#4e7cff'; ctx.lineWidth = 2; ctx.setLineDash([4, 2])
        var b = this._getBounds(sel)
        if (b) { ctx.strokeRect(b.x - 6, b.y - 6, b.w + 12, b.h + 12) }
        ctx.setLineDash([])
      }
    },

    _getBounds: function(el) {
      var s, s2
      switch (el.type) {
        case 'point': s = this.toScreen(el.x, el.y); return { x: s.x - 5, y: s.y - 5, w: 10, h: 10 }
        case 'line': s = this.toScreen(el.x1, el.y1); s2 = this.toScreen(el.x2, el.y2)
          return { x: Math.min(s.x, s2.x), y: Math.min(s.y, s2.y), w: Math.abs(s2.x - s.x), h: Math.abs(s2.y - s.y) }
        case 'circle': s = this.toScreen(el.cx, el.cy); var r = Math.abs(this.toScreen(el.cx + el.r, el.cy).x - s.x)
          return { x: s.x - r, y: s.y - r, w: r * 2, h: r * 2 }
        case 'label': s = this.toScreen(el.x, el.y); return { x: s.x, y: s.y - 14, w: 60, h: 18 }
        default: return null
      }
    },

    // ── 交互 ──────────────────────────────
    setTool: function(t) { if (this.tool === t) return; this.tool = t; this._preview = null; this._angleLines = []; this.dragging = null },

    _findLineAt: function(mx, my) {
      var best = null, bestDist = 12
      for (var i = 0; i < this.elements.length; i++) {
        var el = this.elements[i]
        if (el.type !== 'line') continue
        var s1 = this.toScreen(el.x1, el.y1), s2 = this.toScreen(el.x2, el.y2)
        var d = this._distToSeg(mx, my, s1.x, s1.y, s2.x, s2.y)
        if (d < bestDist) { best = el; bestDist = d }
      }
      return best
    },

    _findAt: function(mx, my) {
      for (var i = this.elements.length - 1; i >= 0; i--) {
        var el = this.elements[i]
        if (el.type === 'point') { var s = this.toScreen(el.x, el.y); if (dist(mx, my, s.x, s.y) < 10) return el }
        if (el.type === 'line') {
          var s1 = this.toScreen(el.x1, el.y1), s2 = this.toScreen(el.x2, el.y2)
          var d = this._distToSeg(mx, my, s1.x, s1.y, s2.x, s2.y)
          if (d < 8) return el
        }
        if (el.type === 'circle') { var sc = this.toScreen(el.cx, el.cy), r = Math.abs(this.toScreen(el.cx + el.r, el.cy).x - sc.x); if (Math.abs(dist(mx, my, sc.x, sc.y) - r) < 10) return el }
        if (el.type === 'label') { var sl = this.toScreen(el.x, el.y); if (Math.abs(mx - sl.x) < 40 && Math.abs(my - sl.y) < 14) return el }
      }
      return null
    },

    _derivative: function(fn, x) { var h = 1e-6; return (fn(x + h) - fn(x - h)) / (2 * h) },

    _distToSeg: function(px, py, x1, y1, x2, y2) {
      var dx = x2 - x1, dy = y2 - y1, len2 = dx*dx + dy*dy
      if (len2 === 0) return dist(px, py, x1, y1)
      var t = Math.max(0, Math.min(1, ((px-x1)*dx + (py-y1)*dy) / len2))
      return dist(px, py, x1 + t*dx, y1 + t*dy)
    },

    _snapToPoint: function(mx, my) {
      for (var i = this.elements.length - 1; i >= 0; i--) {
        var el = this.elements[i]
        if (el.type === 'point') { var s = this.toScreen(el.x, el.y); if (dist(mx, my, s.x, s.y) < 12) return el }
      }
      return null
    },

    onMouseDown: function(e) {
      var rect = this.$refs.c.getBoundingClientRect()
      var s = this.$refs.c.width / rect.width
      var mx = (e.clientX - rect.left) * s, my = (e.clientY - rect.top) * s
      var world = this.toWorld(mx, my)

      if (this.tool === 'move') {
        var hit = this._findAt(mx, my)
        if (hit) {
          if (hit.type === 'point') { this.dragging = hit; this.dragOffset = { dx: world.x - hit.x, dy: world.y - hit.y } }
          else { this.selected = hit; this.draw() }
        } else { this.selected = null; this.draw() }
        return
      }

      if (this.tool === 'tangent') {
        var self3 = this
        // 找最近的函数曲线
        var bestFn = null, bestDist = Infinity, bestX = 0, bestY = 0
        this.functions.forEach(function(fn) {
          if (!fn.fn) return
          for (var tx = -6; tx <= 6; tx += 0.05) {
            var ty = fn.fn(tx)
            if (isNaN(ty) || !isFinite(ty)) continue
            var s = self3.toScreen(tx, ty)
            var d = dist(mx, my, s.x, s.y)
            if (d < bestDist) { bestDist = d; bestFn = fn; bestX = tx; bestY = ty }
          }
        })
        if (bestFn && bestDist < 20) {
          var slope = this._derivative(bestFn.fn, bestX)
          if (isFinite(slope)) {
            var len = 3
            this._pushUndo()
            this.elements.push({ id:++uid, type:'line',
              x1: parseFloat((bestX - len).toFixed(2)), y1: parseFloat((bestY - slope * len).toFixed(2)),
              x2: parseFloat((bestX + len).toFixed(2)), y2: parseFloat((bestY + slope * len).toFixed(2)),
              color: '#e74c3c', lineWidth: 1.5, dashed: true, _tangent: true })
            this.elements.push({ id:++uid, type:'point', x: parseFloat(bestX.toFixed(3)), y: parseFloat(bestY.toFixed(3)), label:'', color:'#e74c3c' })
          }
        } else {
          // 找最近的圆 — 在屏幕坐标中计算切线和切点
          var bestCircle2 = null, bestCDist2 = Infinity
          for (var ci = 0; ci < this.elements.length; ci++) {
            var el2 = this.elements[ci]
            if (el2.type !== 'circle') continue
            var sc2 = this.toScreen(el2.cx, el2.cy)
            var sr2 = Math.abs(this.toScreen(el2.cx + el2.r, el2.cy).x - sc2.x)
            var cd2 = Math.abs(dist(mx, my, sc2.x, sc2.y) - sr2)
            if (cd2 < bestCDist2) { bestCDist2 = cd2; bestCircle2 = el2 }
          }
          if (bestCircle2 && bestCDist2 < 18) {
            var sc = this.toScreen(bestCircle2.cx, bestCircle2.cy)
            var sr = Math.abs(this.toScreen(bestCircle2.cx + bestCircle2.r, bestCircle2.cy).x - sc.x)
            // 在屏幕坐标中求切点
            var ang = Math.atan2(my - sc.y, mx - sc.x)
            var tScreenX = sc.x + sr * Math.cos(ang)
            var tScreenY = sc.y + sr * Math.sin(ang)
            // 切线方向（屏幕）：垂直于半径，长度 150px
            var tdx = -Math.sin(ang), tdy = Math.cos(ang)
            var tLenPx = 150
            var s1x = tScreenX - tdx * tLenPx, s1y = tScreenY - tdy * tLenPx
            var s2x = tScreenX + tdx * tLenPx, s2y = tScreenY + tdy * tLenPx
            // 转回世界坐标存储
            var w1 = this.toWorld(s1x, s1y), w2 = this.toWorld(s2x, s2y), wt = this.toWorld(tScreenX, tScreenY)
            this._pushUndo()
            this.elements.push({ id:++uid, type:'line',
              x1: parseFloat(w1.x.toFixed(3)), y1: parseFloat(w1.y.toFixed(3)),
              x2: parseFloat(w2.x.toFixed(3)), y2: parseFloat(w2.y.toFixed(3)),
              color: '#e74c3c', lineWidth: 1.5, dashed: true, _tangent: true })
            this.elements.push({ id:++uid, type:'point', x: parseFloat(wt.x.toFixed(3)), y: parseFloat(wt.y.toFixed(3)), label:'', color:'#e74c3c' })
          }
        }
        this.draw(); return
      }

      this._pushUndo()

      if (this.tool === 'point') {
        var snap = this._snapToPoint(mx, my)
        var x = snap ? snap.x : parseFloat(world.x.toFixed(2))
        var y = snap ? snap.y : parseFloat(world.y.toFixed(2))
        if (!snap) {
          var label = LABELS[this.labelIdx % 26]; this.labelIdx++
          var exists = false; this.elements.forEach(function(el) { if (el.label === label) exists = true })
          if (exists) label = LABELS[this.labelIdx % 26]; this.labelIdx++
          this.elements.push({ id: ++uid, type: 'point', x: x, y: y, label: label, color: '#409EFF' })
        }
        this.selected = null
      } else if (this.tool === 'line') {
        var p1 = this._snapToPoint(mx, my)
        var lx = p1 ? p1.x : parseFloat(world.x.toFixed(2))
        var ly = p1 ? p1.y : parseFloat(world.y.toFixed(2))
        this._preview = { type: 'line', x1: lx, y1: ly, x2: lx, y2: ly }
        if (!p1) {
          var np = { id: ++uid, type: 'point', x: lx, y: ly, label: LABELS[this.labelIdx % 26], color: '#409EFF' }
          this.labelIdx++; this.elements.push(np); this._preview._p1 = np
        } else { this._preview._p1 = p1 }
      } else if (this.tool === 'circle') {
        var cp = this._snapToPoint(mx, my)
        var cx = cp ? cp.x : parseFloat(world.x.toFixed(2))
        var cy = cp ? cp.y : parseFloat(world.y.toFixed(2))
        this._preview = { type: 'circle', cx: cx, cy: cy, r: 0 }
        if (!cp) {
          var nc = { id: ++uid, type: 'point', x: cx, y: cy, label: LABELS[this.labelIdx % 26], color: '#e74c3c' }
          this.labelIdx++; this.elements.push(nc); this._preview._center = nc
        } else { this._preview._center = cp }
      } else if (this.tool === 'label') {
        this.elements.push({ id: ++uid, type: 'label', x: parseFloat(world.x.toFixed(2)), y: parseFloat(world.y.toFixed(2)), text: '文字', color: '#333' })
        this.selected = null
      } else if (this.tool === 'angle' || this.tool === 'rightAngle') {
        var hitLine = this._findLineAt(mx, my)
        if (!hitLine) return
        // 高亮选中的线
        if (this._angleLines.length === 0) {
          this._angleLines = [hitLine]
          this.draw()
        } else {
          this._angleLines.push(hitLine)
          var l1 = this._angleLines[0], l2 = this._angleLines[1]
          // 找共享顶点（容差 0.3）
          var eps = 0.3, shared = null, a1 = null, a2 = null
          var pts1 = [{x:l1.x1,y:l1.y1},{x:l1.x2,y:l1.y2}]
          var pts2 = [{x:l2.x1,y:l2.y1},{x:l2.x2,y:l2.y2}]
          for (var pi = 0; pi < 2; pi++) {
            for (var pj = 0; pj < 2; pj++) {
              if (Math.abs(pts1[pi].x - pts2[pj].x) < eps && Math.abs(pts1[pi].y - pts2[pj].y) < eps) {
                shared = pts1[pi]; a1 = pts1[1-pi]; a2 = pts2[1-pj]
              }
            }
          }
          if (!shared) { this._angleLines = []; this.draw(); return }
          if (this.tool === 'rightAngle') {
            this._pushUndo()
            this.elements.push({ id:++uid, type:'rightAngle', vx:shared.x, vy:shared.y, ax:a1.x, ay:a1.y, bx:a2.x, by:a2.y, color:'#333' })
          } else {
            var self2 = this
            // 查找同顶点已有的角度标注，用于回填默认值
            var existingArc = null
            for (var ei = 0; ei < self2.elements.length; ei++) {
              var e = self2.elements[ei]
              if (e.type === 'angleArc' && Math.abs(e.vx - shared.x) < 0.01 && Math.abs(e.vy - shared.y) < 0.01) {
                existingArc = e; break
              }
            }
            this.$prompt('请输入角度值（°）', '标注角度', { inputValue: existingArc ? String(existingArc.deg) : '' }).then(function(r) {
              if (r.value && !isNaN(parseFloat(r.value))) {
                if (existingArc) {
                  existingArc.deg = parseFloat(r.value)
                } else {
                  self2._pushUndo()
                  self2.elements.push({ id:++uid, type:'angleArc', vx:shared.x, vy:shared.y, ax:a1.x, ay:a1.y, bx:a2.x, by:a2.y, deg:parseFloat(r.value), color:'#333' })
                }
              }
              self2._angleLines = []
              self2.draw()
            }).catch(function() { self2._angleLines = []; self2.draw() })
          }
        }
      }
      this.draw()
    },

    onMouseMove: function(e) {
      var rect = this.$refs.c.getBoundingClientRect()
      var s = this.$refs.c.width / rect.width
      var mx = (e.clientX - rect.left) * s, my = (e.clientY - rect.top) * s
      var world = this.toWorld(mx, my)
      this.cursor = '(' + world.x.toFixed(2) + ', ' + world.y.toFixed(2) + ')'

      if (this.dragging) {
        this.dragging.x = parseFloat((world.x - this.dragOffset.dx).toFixed(2))
        this.dragging.y = parseFloat((world.y - this.dragOffset.dy).toFixed(2))
        this.draw()
      }
      // 画线/画圆预览
      if (this._preview) {
        if (this._preview.type === 'line') {
          this._preview.x2 = parseFloat(world.x.toFixed(2))
          this._preview.y2 = parseFloat(world.y.toFixed(2))
        } else if (this._preview.type === 'circle') {
          this._preview.r = parseFloat(dist(this._preview.cx, this._preview.cy, world.x, world.y).toFixed(2))
        }
        this.draw()
      }
    },

    onMouseUp: function(e) {
      if (!e || !this.tool || this.tool === 'move' || this.tool === 'point' || this.tool === 'label') { this.dragging = null; return }
      var rect = this.$refs.c.getBoundingClientRect()
      var s = this.$refs.c.width / rect.width
      var mx = (e.clientX - rect.left) * s, my = (e.clientY - rect.top) * s
      var world = this.toWorld(mx, my)

      if (this.tool === 'line' && this._preview && this._preview.type === 'line') {
        var p2 = this._snapToPoint(mx, my)
        var ex = p2 ? p2.x : parseFloat(world.x.toFixed(2))
        var ey = p2 ? p2.y : parseFloat(world.y.toFixed(2))
        this._pushUndo()
        if (!p2) {
          var np = { id: ++uid, type: 'point', x: ex, y: ey, label: LABELS[this.labelIdx % 26], color: '#409EFF' }
          this.labelIdx++; this.elements.push(np)
        }
        this.elements.push({ id: ++uid, type: 'line', x1: this._preview.x1, y1: this._preview.y1, x2: ex, y2: ey, color: '#333', lineWidth: 2 })
        this._preview = null
      } else if (this.tool === 'circle' && this._preview && this._preview.type === 'circle') {
        var rp = this._snapToPoint(mx, my)
        var rx = rp ? rp.x : parseFloat(world.x.toFixed(2))
        var ry = rp ? rp.y : parseFloat(world.y.toFixed(2))
        var r = parseFloat(dist(this._preview.cx, this._preview.cy, rx, ry).toFixed(2))
        if (r > 0.05) {
          this._pushUndo()
          this.elements.push({ id: ++uid, type: 'circle', cx: this._preview.cx, cy: this._preview.cy, r: r, color: '#333', lineWidth: 2 })
        }
        this._preview = null
      }
      this.dragging = null
      this.draw()
    },

    onDblClick: function(e) {
      var rect = this.$refs.c.getBoundingClientRect()
      var s = this.$refs.c.width / rect.width
      var mx = (e.clientX - rect.left) * s, my = (e.clientY - rect.top) * s
      var hit = this._findAt(mx, my)
      if (hit) {
        this.selected = hit
        if (hit.type === 'label') {
          var self = this
          this.$prompt('编辑标签文字', '编辑', { inputValue: hit.text }).then(function(r) {
            if (r.value) { hit.text = r.value; self.draw() }
          })
        }
      } else { this.selected = null }
      this.draw()
    },

    onWheel: function(e) { /* 暂不实现缩放 */ },

    deleteSelected: function() {
      if (!this.selected) return
      var idx = this.elements.indexOf(this.selected)
      if (idx >= 0) this.elements.splice(idx, 1)
      this.selected = null; this.draw()
    },

    // ── 撤销 ──────────────────────────────
    _pushUndo: function() { this.undoStack.push(JSON.stringify(this.elements)); if (this.undoStack.length > 50) this.undoStack.shift() },
    undo: function() {
      if (!this.undoStack.length) return
      try { this.elements = JSON.parse(this.undoStack.pop()); this.draw() } catch(e) {}
    },

    // ── 模板 ──────────────────────────────
    loadTemplate: function(name) {
      this._pushUndo()
      var templates = {
        rightTri: { elements: [
          { id:++uid,type:'point',x:-3,y:-2,label:'A',color:'#409EFF' },
          { id:++uid,type:'point',x:3,y:-2,label:'B',color:'#409EFF' },
          { id:++uid,type:'point',x:-3,y:2,label:'C',color:'#409EFF' },
          { id:++uid,type:'line',x1:-3,y1:-2,x2:3,y2:-2,color:'#333' },
          { id:++uid,type:'line',x1:-3,y1:-2,x2:-3,y2:2,color:'#333' },
          { id:++uid,type:'line',x1:3,y1:-2,x2:-3,y2:2,color:'#333' }
        ]},
        isosceles: { elements: [
          { id:++uid,type:'point',x:-3,y:-2,label:'A',color:'#409EFF' },
          { id:++uid,type:'point',x:3,y:-2,label:'B',color:'#409EFF' },
          { id:++uid,type:'point',x:0,y:3,label:'C',color:'#409EFF' },
          { id:++uid,type:'line',x1:-3,y1:-2,x2:3,y2:-2,color:'#333' },
          { id:++uid,type:'line',x1:3,y1:-2,x2:0,y2:3,color:'#333' },
          { id:++uid,type:'line',x1:0,y1:3,x2:-3,y2:-2,color:'#333' }
        ]},
        equilateral: { elements: [
          { id:++uid,type:'point',x:-3,y:-2,label:'A',color:'#409EFF' },
          { id:++uid,type:'point',x:3,y:-2,label:'B',color:'#409EFF' },
          { id:++uid,type:'point',x:0,y:3.2,label:'C',color:'#409EFF' },
          { id:++uid,type:'line',x1:-3,y1:-2,x2:3,y2:-2,color:'#333' },
          { id:++uid,type:'line',x1:3,y1:-2,x2:0,y2:3.2,color:'#333' },
          { id:++uid,type:'line',x1:0,y1:3.2,x2:-3,y2:-2,color:'#333' }
        ]},
        square: { elements: [
          { id:++uid,type:'point',x:-2,y:-2,label:'A',color:'#409EFF' },
          { id:++uid,type:'point',x:2,y:-2,label:'B',color:'#409EFF' },
          { id:++uid,type:'point',x:2,y:2,label:'C',color:'#409EFF' },
          { id:++uid,type:'point',x:-2,y:2,label:'D',color:'#409EFF' },
          { id:++uid,type:'line',x1:-2,y1:-2,x2:2,y2:-2,color:'#333' },
          { id:++uid,type:'line',x1:2,y1:-2,x2:2,y2:2,color:'#333' },
          { id:++uid,type:'line',x1:2,y1:2,x2:-2,y2:2,color:'#333' },
          { id:++uid,type:'line',x1:-2,y1:2,x2:-2,y2:-2,color:'#333' }
        ]},
        circle: { elements: [
          { id:++uid,type:'point',x:0,y:0,label:'O',color:'#e74c3c' },
          { id:++uid,type:'circle',cx:0,cy:0,r:2.5,color:'#333' },
          { id:++uid,type:'line',x1:0,y1:0,x2:2.5,y2:0,color:'#999',dashed:true }
        ]},
      }
      var tpl = templates[name]
      if (tpl && tpl.elements) {
        tpl.elements.forEach(function(el) { el.id = ++uid })
        this.elements = JSON.parse(JSON.stringify(tpl.elements))
      }
      this.labelIdx = this.elements.filter(function(el) { return el.type === 'point' }).length
      this.selected = null
      this.draw()
    },

    clearAll: function() { this.elements = []; this.functions = []; this.selected = null; this.labelIdx = 0; this.fnColorIdx = 0; this.draw() },

    // ── 函数表达式 ────────────────────────
    compile: function(body) {
      body = body.replace(/\^/g, '**')
        .replace(/(\d)([a-zA-Z])/g, '$1*$2')
        .replace(/\)\(/g, ')*(').replace(/(\d)\(/g, '$1*(')
        .replace(/\)(\d)/g, ')*$1')
        .replace(/(?<!\w)([a-zA-Z])\(/g, '$1*(')  // 仅单字母变量后跟括号才补乘号，不动 sin( cos( 等
      try {
        return new Function('x', 'const { sin,cos,tan,sqrt,abs,log,exp,pow,PI,E,floor,ceil,round,sign,log2,log10,max,min } = Math; return (' + body + ')')
      } catch(e) { return null }
    },

    addFunction: function() {
      var expr = this.fnInput.trim(); if (!expr) return
      var fn = this.compile(expr); if (!fn) return
      this.functions.push({ id: ++uid, expr: expr, fn: fn, color: COLORS[this.fnColorIdx % COLORS.length] })
      this.fnColorIdx++; this.fnInput = ''
      this.draw()
    },

    // ── 导出 ──────────────────────────────
    exportPng: function() {
      var c = this.$refs.c
      var a = document.createElement('a')
      a.href = c.toDataURL('image/png'); a.download = 'geometry.png'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    },

    exportSvg: function() {
      var self = this, lines = ['<svg xmlns="http://www.w3.org/2000/svg" width="' + this.W + '" height="' + this.H + '">']
      lines.push('<rect width="' + this.W + '" height="' + this.H + '" fill="#fff"/>')
      this.elements.forEach(function(el) {
        var s, s2, r
        switch (el.type) {
          case 'point': s = self.toScreen(el.x, el.y); lines.push('<circle cx="' + s.x.toFixed(1) + '" cy="' + s.y.toFixed(1) + '" r="4" fill="' + (el.color||'#409EFF') + '"/>'); if(el.label) lines.push('<text x="' + (s.x+8).toFixed(1) + '" y="' + (s.y-4).toFixed(1) + '" font-size="13" font-weight="bold">' + el.label + '</text>'); break
          case 'line': s = self.toScreen(el.x1, el.y1); s2 = self.toScreen(el.x2, el.y2)
            lines.push('<line x1="' + s.x.toFixed(1) + '" y1="' + s.y.toFixed(1) + '" x2="' + s2.x.toFixed(1) + '" y2="' + s2.y.toFixed(1) + '" stroke="' + (el.color||'#333') + '" stroke-width="' + (el.lineWidth||2) + '"' + (el.dashed?' stroke-dasharray="6,4"':'') + '/>'); break
          case 'circle': s = self.toScreen(el.cx, el.cy); r = (self.toScreen(el.cx + el.r, el.cy).x - s.x).toFixed(1)
            lines.push('<circle cx="' + s.x.toFixed(1) + '" cy="' + s.y.toFixed(1) + '" r="' + Math.abs(r) + '" fill="none" stroke="' + (el.color||'#333') + '" stroke-width="' + (el.lineWidth||2) + '"/>'); break
          case 'label': s = self.toScreen(el.x, el.y); lines.push('<text x="' + s.x.toFixed(1) + '" y="' + s.y.toFixed(1) + '" font-size="13">' + (el.text||'') + '</text>'); break
          case 'angleArc':
            var av = self.toScreen(el.vx, el.vy), aa = self.toScreen(el.ax, el.ay), ab = self.toScreen(el.bx, el.by)
            var aa1 = Math.atan2(aa.y - av.y, aa.x - av.x), aa2 = Math.atan2(ab.y - av.y, ab.x - av.x)
            var ar = 30, ax1 = av.x + ar*Math.cos(aa1), ay1 = av.y + ar*Math.sin(aa1), ax2 = av.x + ar*Math.cos(aa2), ay2 = av.y + ar*Math.sin(aa2)
            var swpCw2 = (aa1 - aa2 + 2*Math.PI) % (2*Math.PI)
            var amid2, sweepFlag2
            if (swpCw2 <= Math.PI) { sweepFlag2 = 1; amid2 = aa1 + swpCw2/2 }
            else { sweepFlag2 = 0; amid2 = aa1 - (2*Math.PI - swpCw2)/2 }
            lines.push('<path d="M' + ax1.toFixed(1) + ',' + ay1.toFixed(1) + ' A' + ar + ',' + ar + ' 0 0,' + sweepFlag2 + ' ' + ax2.toFixed(1) + ',' + ay2.toFixed(1) + '" fill="none" stroke="' + (el.color||'#333') + '" stroke-width="1.5"/>')
            var alx = av.x+(ar+10)*Math.cos(amid2), aly = av.y+(ar+10)*Math.sin(amid2)
            lines.push('<text x="' + alx.toFixed(1) + '" y="' + aly.toFixed(1) + '" font-size="12" font-weight="bold" text-anchor="middle">' + el.deg + '°</text>')
            break
          case 'rightAngle':
            var rrv = self.toScreen(el.vx, el.vy), rra = self.toScreen(el.ax, el.ay), rrb = self.toScreen(el.bx, el.by)
            var rra1 = Math.atan2(rra.y-rrv.y, rra.x-rrv.x), rra2 = Math.atan2(rrb.y-rrv.y, rrb.x-rrv.x)
            var rrs = 14, rrx1=rrv.x+rrs*Math.cos(rra1), rry1=rrv.y+rrs*Math.sin(rra1), rrx2=rrv.x+rrs*Math.cos(rra2), rry2=rrv.y+rrs*Math.sin(rra2)
            var rrx3=rrx1+rrs*Math.cos(rra2), rry3=rry1+rrs*Math.sin(rra2)
            lines.push('<polyline points="' + rrx1.toFixed(1)+','+rry1.toFixed(1) + ' ' + rrx3.toFixed(1)+','+rry3.toFixed(1) + ' ' + rrx2.toFixed(1)+','+rry2.toFixed(1) + '" fill="none" stroke="' + (el.color||'#333') + '" stroke-width="1.5"/>')
            break
        }
      })
      // 函数曲线
      var pad = 40, self3 = this
      this.functions.forEach(function(fn) {
        if (!fn.fn) return
        var d = '', started = false, step = 12 / 400
        for (var x = -6; x <= 6; x += step) {
          var y = fn.fn(x)
          if (isNaN(y) || !isFinite(y)) { started = false; continue }
          var sx = self3.toScreen(x, y).x, sy = self3.toScreen(x, y).y
          if (!started) { d += 'M' + sx.toFixed(1) + ',' + sy.toFixed(1); started = true }
          else d += 'L' + sx.toFixed(1) + ',' + sy.toFixed(1)
        }
        lines.push('<path d="' + d + '" fill="none" stroke="' + fn.color + '" stroke-width="2"/>')
      })
      lines.push('</svg>')
      var blob = new Blob([lines.join('\n')], { type: 'image/svg+xml' })
      var url = URL.createObjectURL(blob)
      var a = document.createElement('a'); a.href = url; a.download = 'geometry.svg'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    doAction: function(a) { if (a === 'clear') this.clearAll() }
  }
}
</script>

<style scoped>
.gd-root { max-width: 820px; margin: 0 auto; }

.gd-topbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; flex-wrap: wrap; }
.gd-info { display: flex; flex-direction: column; gap: 2px; }
.gd-title { font-size: 15px; font-weight: 700; color: #1a1f36; }
.gd-hint { font-size: 11px; color: #b0b8c4; }
.gd-actions { display: flex; gap: 6px; flex-wrap: wrap; }

.gd-toolbar { display: flex; align-items: center; gap: 4px; margin-bottom: 8px; padding: 6px 10px; background: #fff; border: 1px solid #e4e7ed; border-radius: 6px; }
.gd-toolbar button {
  padding: 4px 10px; border: 1px solid #dcdfe6; border-radius: 4px; background: #fff;
  font-size: 13px; cursor: pointer; transition: all .12s; color: #606266;
}
.gd-toolbar button:hover { border-color: #4e7cff; color: #4e7cff; }
.gd-toolbar button.on { background: #4e7cff; color: #fff; border-color: #4e7cff; }
.gd-tb-label { font-size: 12px; color: #8892a6; font-weight: 600; margin-right: 4px; }
.gd-tb-sep { color: #e4e7ed; margin: 0 4px; }
.gd-tb-check { font-size: 12px; color: #606266; cursor: pointer; display: flex; align-items: center; gap: 3px; }
.gd-tb-check input { margin: 0; }
.gd-tb-hint { font-size: 11px; color: #c0c4cc; margin-left: auto; }

.gd-templates { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
.gd-tpl-label { font-size: 12px; color: #8892a6; font-weight: 600; }

.gd-canvas-wrap { position: relative; }
.gd-canvas-wrap canvas { width: 100%; height: auto; display: block; border: 1px solid #e4e7ed; border-radius: 8px; background: #fff; cursor: crosshair; }
.gd-cursor { position: absolute; top: 8px; right: 12px; font-size: 11px; color: #4e7cff; font-family: monospace; background: rgba(255,255,255,.9); padding: 3px 8px; border-radius: 4px; border: 1px solid #e4e7ed; pointer-events: none; }

.gd-fn-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.gd-fn-label { font-size: 16px; font-weight: 700; color: #1a1f36; font-style: italic; flex-shrink: 0; }
.gd-fn-presets { display: flex; gap: 4px; flex-wrap: wrap; }
.gd-fn-chip { font-size: 11px; padding: 1px 7px; border-radius: 3px; cursor: pointer; background: #f5f6f8; color: #606266; border: 1px solid #e4e7ed; font-family: monospace; line-height: 22px; }
.gd-fn-chip:hover { border-color: #4e7cff; color: #4e7cff; background: #f0f4ff; }
.gd-fn-list { display: flex; flex-direction: column; gap: 2px; margin-top: 6px; }
.gd-fn-item { display: flex; align-items: center; gap: 8px; }
.gd-fn-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
.gd-fn-expr { font-size: 12px; font-family: monospace; color: #606266; }
.gd-fn-item .del { color: #f56c6c; }

.gd-props { display: flex; align-items: center; gap: 8px; margin-top: 8px; padding: 6px 12px; background: #f0f4ff; border-radius: 6px; }
.gd-prop-label { font-size: 12px; color: #4e7cff; font-weight: 600; }
</style>
